import { useFormikContext } from 'formik';
import { ChangeEvent, useRef, useState } from 'react';

import { Button } from '../../../../cdl/Button/Button';
import { Callout } from '../../../../cdl/Callout/Callout';
import { IconButton } from '../../../../cdl/IconButton/IconButton';
import { Text } from '../../../../cdl/Text/Text';
import { TextButton } from '../../../../common/buttons/TextButton';
import { translate } from '../../../../common/helpers/translate.helper';
import { IconInfoCircle } from '../../../../common/icons/cdl/InfoCircle';
import { IconTrash } from '../../../../common/icons/cdl/Trash';
import { Box } from '../../../../common/ui/Box';
import { FormikVesselDetailsState } from '../EditVesselForm';

interface FormikVesselAttachmentsSectionProps {
    lubricationChart?: {
        name: string;
        downloadUrl: string;
    };
}

export const FormikVesselLubricationChartSection = ({ lubricationChart }: FormikVesselAttachmentsSectionProps) => {
    const { setFieldValue } = useFormikContext<FormikVesselDetailsState>();
    const [fileData, setFileData] = useState({
        name: lubricationChart?.name,
        downloadUrl: lubricationChart?.downloadUrl,
    });
    const fileInputRef = useRef<HTMLInputElement>(null);

    const onChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        setFileData({ name: file.name, downloadUrl: undefined });

        setFieldValue('lubricationChart', {
            file,
            added: true,
            removed: false,
        });
    };

    const onRemove = () => {
        setFileData({ name: undefined, downloadUrl: undefined });

        setFieldValue('lubricationChart', {
            file: null,
            added: false,
            removed: !!lubricationChart?.name,
        });
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <Box display="grid" paddingBottom={!fileData.name ? 40 : 0}>
            <Text variant="subtitle1">{translate('vessel.edit.attachments')}</Text>

            <Box display="flex" flexDirection="column" gap={8} alignItems="start" marginTop={16}>
                <Text variant="fieldLabel" color="foreground.muted">
                    {translate('vessel.edit.lubricationChart')}
                </Text>
                <Callout Icon={IconInfoCircle} description={translate('vessel.edit.lubricationChartNote')} />
                <input
                    type="file"
                    onChange={onChange}
                    style={{
                        display: 'none',
                    }}
                    ref={fileInputRef}
                />
                <Button type="button" onClick={handleUploadClick}>
                    {translate('vessel.edit.uploadFile')}
                </Button>
            </Box>

            {fileData.name ? (
                <Box display="flex" alignItems="center" justifyContent="space-between" marginTop={8}>
                    {fileData.downloadUrl ? (
                        <a
                            href={fileData.downloadUrl}
                            download
                            target="_blank"
                            rel="noreferrer"
                            style={{ textDecoration: 'none' }}
                        >
                            <TextButton
                                type="button"
                                style={{
                                    wordBreak: 'break-all',
                                }}
                            >
                                {fileData.name}
                            </TextButton>
                        </a>
                    ) : (
                        <Text variant="fieldLabel">{fileData.name}</Text>
                    )}
                    <IconButton type="button" Icon={IconTrash} onClick={onRemove} />
                </Box>
            ) : null}
        </Box>
    );
};
